<script>
import pagination from "@/mixins/pagination";

import debounce from "debounce";
import userService from "@/services/user";
import inviteService from "@/services/invite";
import helperService from "@/services/helper";
import segmentService from "@/services/segment";
import { USER_ROLES } from "@/services/constants";

import Spinner from "@/components/ui/Spinner";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";

const DEBOUNCE_MS_TIMEOUT = 500;
const { MANAGER, ADMIN, ASSISTANT, SCORES, ADMISSIONIST } = USER_ROLES;

export default {
  name: "UserList",

  mixins: [pagination],

  components: {
    Spinner,
    BaseSubNav,
  },

  data() {
    return {
      isLoading: false,
      searchInput: "",
      users: [],
      filterRoleIds: [],
    };
  },

  computed: {
    loggedUserRole() {
      return this.$store.state.user.role;
    },

    loggedUserId() {
      return this.$store.state.user._id;
    },

    usedRoles() {
      if (this.loggedUserRole === MANAGER.name) {
        return [MANAGER, ADMISSIONIST];
      }

      if (this.loggedUserRole === ADMIN.name) {
        return [MANAGER, ADMIN, ASSISTANT, SCORES, ADMISSIONIST];
      }

      return [];
    },
  },

  created() {
    this.filterRoleIds = this.usedRoles.map((r) => r.id);
    this.getUsers();

    this.$bus.$on("user-update", (user) => {
      const index = this.users.findIndex((u) => u._id === user._id);
      if (index >= 0) {
        this.$set(this.users, index, user);
      } else {
        this.users.push(user);
      }
    });
  },

  methods: {
    getUsers() {
      this.isLoading = true;

      let defaultQuery = {
        offset: this.pagination.offset,
        limit: this.pagination.limit,
        roles: this.filterRoleIds,
      };

      //This is used when you make a search or change pages
      if (this.searchInput) {
        defaultQuery = { ...defaultQuery, searchInput: this.searchInput };
        defaultQuery = helperService.buildQuery(defaultQuery);
      }

      const searchQuery = defaultQuery;

      userService
        .get(searchQuery)
        .then((res) => {
          this.users = res.docs;
          this.pagination.total = res.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    debounceInput: debounce(function () {
      this.getUsers();
    }, DEBOUNCE_MS_TIMEOUT),

    editUser(id) {
      helperService.callIfNoSelectedText(() =>
        this.$router.push({
          name: "update-user",
          params: { id },
        })
      );
    },

    lockUser(user, index) {
      this.$confirm(
        `¿Desea bloquear al usuario/a: ${helperService.getFullName(user)}?`,
        "Bloquear",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => {
          segmentService.track({ name: "User Locked" });
          userService.lock(user._id).then(() => {
            this.$set(this.users, index, {
              ...this.users[index],
              lockedAt: new Date(),
            });
          });
        })
        .catch(() => {});
    },

    unlockUser(id, index) {
      segmentService.track({ name: "User Unlocked" });
      userService.unlock(id).then(() => {
        this.$set(this.users, index, {
          ...this.users[index],
          lockedAt: undefined,
        });
      });
    },

    isLocked(user) {
      return user.lockedAt;
    },

    deleteUser(user) {
      this.$confirm(
        `¿Desea eliminar al usuario/a: ${helperService.getFullName(user)}?`,
        "Bloquear",
        {
          confirmButtonText: "Aceptar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      )
        .then(() => {
          segmentService.track({ name: "User Deleted" });
          return userService.delete(user._id);
        })
        .then(() => this.getUsers())
        .catch(() => {});
    },

    reSendInvite(username) {
      inviteService
        .sendInvite(username)
        .then(() =>
          this.$message.success(`Se reenvío una invitación a ${username}`)
        );
    },

    /**
     * @description UC-REG-096 — Envía email de blanqueo de clave al usuario.
     *              Usa el flujo password-reset estándar (PUT
     *              /users/:username/password-reset). El usuario recibe link
     *              para fijar una clave nueva.
     * @param {Object} user
     */
    resetPassword(user) {
      if (!user.username) {
        this.$message.warning("El usuario no tiene username asignado.");
        return;
      }
      this.$confirm(
        `Se enviará un email a ${
          user.email || user.username
        } con un link para fijar una clave nueva. ¿Continuar?`,
        "Blanquear clave",
        {
          confirmButtonText: "Enviar",
          cancelButtonText: "Cancelar",
          type: "warning",
        }
      ).then(() => {
        userService
          .sendPasswordReset(user.username)
          .then(() =>
            this.$message.success("Email de blanqueo de clave enviado")
          )
          .catch(() => this.$message.error("No se pudo enviar el blanqueo"));
      });
    },

    getUserRoleName(roleID) {
      const roleKey = Object.keys(USER_ROLES).find(
        (key) => USER_ROLES[key].id === roleID
      );
      return roleKey && USER_ROLES[roleKey].name;
    },

    onPageChange() {
      this.getUsers();
    },

    getFullName(user) {
      return helperService.getFullName(user);
    },
  },
};
</script>

<template lang="pug">
  section.user-list
    BaseSubNav
      template(#title)
        | Usuarios
        small(v-if="pagination.total")  ({{ pagination.total }})
      template(slot="actions")
          el-checkbox-group(v-model="filterRoleIds")
            el-checkbox(
              v-for="role in usedRoles"
              v-if="usedRoles.length > 1"
              v-auth="`${role.key}.query`"
              @change='debounceInput'
              :key="role.id"
              :label="role.id"
            ) {{ role.alias || role.name }}
          el-input.search__input(
            prefix-icon="el-icon-search"
            @input="debounceInput"
            v-model="searchInput"
            placeholder="Buscar"
            :clearable="true"
          )
          router-link.button.button--blue(:to="{name: 'create-user'}" v-auth="['doctor.create', 'manager.create', 'assistan.create', 'admin.create']")
            micon(name="add_circle_outline").button__icon
            span.button__text Nuevo Usuario
    .user-list__info-banner(v-if="loggedUserRole === 'admin'")
      micon(name="info" size="16")
      span Los prestadores (médicos) se gestionan desde&nbsp;
      router-link(:to="{ name: 'providers' }") Prestadores
    .box
      .box__content--stretch
        table
          thead
            tr
              th Nombre
              th Usuario
              th Email
              th Telefono
              th Ciudad
              th Rol
              th Estado
              th(style="white-space:nowrap") Acciones
          tbody
            spinner(v-if="isLoading")
            tr(v-else v-for="(user, index) in users" :key="user._id" @click="editUser(user._id)")
              td {{ getFullName(user) }}
              td {{ user.username }}
              td {{ user.email }}
              td {{ user.phone }}
              td {{ user.city }}
              td {{ getUserRoleName(user.role) }}
              td(v-if='isLocked(user)') 
                micon(name="lock")
              td(v-else) 
                micon(name="lock_open")
              td.actions
                .actions-container
                  el-dropdown(trigger="click")
                    .button.button--action.el-dropdown-link(@click.stop)
                      micon(name="more_horiz")
                    el-dropdown-menu(slot="dropdown")
                      el-dropdown-item(v-auth="['doctor.update', 'manager.update', 'assistan.update', 'admin.update']")
                        div(@click="editUser(user._id)")
                          micon(name="edit")
                          | Editar usuario
                      el-dropdown-item(v-if="(loggedUserId !== user._id) && !user.lockedAt" v-auth="['doctor.update', 'manager.update', 'assistan.update', 'admin.update']")
                        div(@click="lockUser(user, index)")
                          micon(name="lock")
                          | Bloquear usuario
                      el-dropdown-item(v-if="loggedUserId !== user._id && user.lockedAt" v-auth="['doctor.update', 'manager.update', 'assistan.update', 'admin.update']")
                        div(@click="unlockUser(user._id, index)")
                          micon(name="lock_open")
                          | Desbloquear usuario
                      //el-dropdown-item(v-if="loggedUserId !== user._id" v-auth="['doctor.remove', 'manager.remove', 'assistan.remove', 'admin.remove']")
                        div(@click="deleteUser(user)")
                          micon(name="delete_forever")
                          | Eliminar usuario
                      //- UC-REG-096: sin condicion !passwordSetAt — siempre
                      //- disponible para casos como UC-REG-091 donde el
                      //- token caduco. Tambien agregamos blanquear clave.
                      el-dropdown-item(v-auth="['doctor.update', 'manager.update', 'assistan.update', 'admin.update']")
                        div(@click="reSendInvite(user.username)")
                          micon(name="email")
                          | Reenviar invitación
                      el-dropdown-item(v-auth="['doctor.update', 'manager.update', 'assistan.update', 'admin.update']")
                        div(@click="resetPassword(user)")
                          micon(name="vpn_key")
                          | Blanquear clave

      pagination(
        :isLoading="isLoading"
        :limit="pagination.limit"
        :total="pagination.total"
        @pagination="setPagination"
      )
</template>

<style lang="scss" scoped>
@import "@/styles/settings/index.scss";
@import "@/styles/tools/index.scss";

.user-list__info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: $teal-light;
  border: 1px solid #d1faf0;
  border-radius: 8px;
  margin-bottom: 16px;
  font-size: 13px;
  color: $text-secondary-new;

  a {
    color: $navy;
    font-weight: 600;
  }
}

tbody td:hover {
  cursor: pointer;
}

.user-list {
  .headline {
    .headline__title {
      @include media(desktop-up) {
        margin: 0 15px 15px 0;
      }
      @include media(web-up) {
        margin: 0 15px 0 0;
      }
    }
    .headline__actions {
      @include media(mobile-up) {
        width: 100%;
      }
      @include media(web-up) {
        width: auto;
      }

      .el-checkbox-group {
        justify-content: space-between;
        margin: 0;

        > * {
          width: calc(50% - 7.5px);
          margin: 0 0 10px;
        }

        @include media(mobile-up) {
          width: 100%;
        }
        @include media(tablet-up) {
          width: auto;
        }
        @include media(ipad-up) {
          align-items: center;
          > * {
            width: auto;
            margin: 0 10px 0 0;
            &:last-child {
              margin: 0;
            }
          }
        }
      }
    }
  }
}
</style>
